<template>
  <div class="page-section">
    <h1 class="info-title" style="padding-top: 60px">
        Your cashflow <b>opportunities</b>
    </h1>
    <p class="section-subtitle">
        Here are the top recommendations for your account
    </p>
    <div class="cards-container">
        <div v-if="shouldShowMortgate" class="breakdown-table">
          <div class="icon-container">
              <img src="../../../../assets/syndesi/home-icon.svg"/>
          </div>
          <div class="breakdown-content">
            <div class="breakdown-row">
              <div class="col title">
                You might be paying too much on your mortgage.
              </div>
            </div>

            <div class="breakdown-row">
              <div class="col subtitle">
                It looks like you're currently paying
                <b>{{ mortgageDisplayText.amount }}</b>{{ mortgageDisplayText.suffix }} on your mortgage.
                <br />
                <br />
                <b>Our mortgage experts could help you save.</b>
              </div>
            </div>
          </div>
          <button class="light-blue-button" @click="handleMortgageButtonClick">
            Book a mortgage review <span class="arrow-right">→</span>
          </button>
        </div>

        <div v-if="shouldShowInsurance" class="breakdown-table">
          <div class="icon-container">
              <img src="../../../../assets/syndesi/insurance-icon.svg"/>
          </div>
          <div class="breakdown-content">
            <div class="breakdown-row">
              <div class="col title">
                There’s a saving opportunity on your insurance payments.
              </div>
            </div>

            <div class="breakdown-row">
              <div class="col subtitle">
                It looks like you're currently paying
                <b>{{ insuranceDisplayText.amount }}</b>{{ insuranceDisplayText.suffix }} on insurance.
                <b>Our insurance experts might help you save.</b>
              </div>
            </div>
          </div>
          <button class="light-blue-button" @click="handleInsuranceButtonClick">
            Connect with an expert <span class="arrow-right">→</span>
          </button>
        </div>

        <div class="breakdown-table">
          <div class="icon-container">
              <img src="../../../../assets/syndesi/heart-check-icon.svg"/>
          </div>
          <div class="breakdown-content">
            <div class="breakdown-row">
              <div class="col title">
                Your future deserves more attention
              </div>
            </div>

            <div class="breakdown-row">
              <div class="col subtitle">
                Not contributing to a pension regularly? Not sure if you could contribute more? Let’s fix that.
              </div>
            </div>
          </div>
          <button class="light-blue-button" @click="handlePensionButtonClick">
            Connect with an expert <span class="arrow-right">→</span>
          </button>
        </div>
    </div>
    <div class="light-yellow-background-color" />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { WhiteLabelFirmIdType } from '../../../../types/otsConfig';
import { numberToHumanReadable } from '../../../../utils/miscUtils';

const props = defineProps({
  mortgages: {
    type: Number,
    default: 0,
  },
  insurances: {
    type: Number,
    default: 0,
  },
  whiteLabelFirmId: {
    type: String,
    default: WhiteLabelFirmIdType.DNS,
  },
});

const isDefaultFirm = computed(() => props.whiteLabelFirmId === WhiteLabelFirmIdType.DefaultFirm);

const shouldShowMortgate = computed(() => props.mortgages > 0 || isDefaultFirm.value);
const shouldShowInsurance = computed(() => props.insurances > 0 || isDefaultFirm.value);

const createDisplayText = amount => {
  if (isDefaultFirm.value && amount === 0) {
    return { amount: 'monthly', suffix: '' };
  }
  return { amount: `£${numberToHumanReadable(amount)}`, suffix: ' per month' };
};

const mortgageDisplayText = computed(() => createDisplayText(props.mortgages));

const insuranceDisplayText = computed(() => createDisplayText(props.insurances));

const emit = defineEmits(['mortgageClick', 'pensionsClick', 'insuranceClick']);

const handleMortgageButtonClick = () => {
  emit('mortgageClick');
};

const handlePensionButtonClick = () => {
  emit('pensionsClick');
};

const handleInsuranceButtonClick = () => {
  emit('insuranceClick');
};
</script>

<style lang="scss" scoped>
@import '../../../../scss/riseup-vars.scss';

.page-section {
  background-color: #205441;
  padding-bottom: 30px;
  padding-top: 15px;
  color: #202125;
  position: relative;
  overflow: hidden
}

.info-title {
  font-size: 36px;
  color: #FFFFFF;
  padding-top: 40px;
  padding-right: 25px;
  margin-bottom: 20px;
  text-align: left;
  width: 80%;
  margin-left: auto;
  margin-right: auto;
  font-weight: 100 !important;
  line-height: 1;
}

.info-title b {
  font-weight: 700;
}

.cards-container {
  position: relative;
  z-index: 2;
  padding-bottom: -50px;
}

.section-subtitle {
  font-size: 17px;
  color: #FFFFFF;
  margin-top: 25px;
  margin-bottom: 40px;
  text-align: left;
  width: 80%;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
}

.light-blue-button {
  background: #5D7AFD;
  box-shadow: 0px 4px 21px rgba(149.13, 152.94, 149.88, 0.56);
  border-radius: 12px;
  color: white;
  padding: 17px 0px;
  border: none;
  width: 100%;
  cursor: pointer;
  font-size: 18px;
  font-weight: 700;
  margin-top: 15px;
  margin-bottom: 10px;
  margin-left: auto;
  margin-right: auto;
  display: block;
  padding-top: 16px;
  padding-bottom: 18px;
}

.icon-container {
  background-color: #5D7AFD;
  border-radius: 20px;
  padding: 10px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
}

.breakdown-table {
  width: 90%;
  background-color: white;
  box-shadow: 0px 16px 46.4px 0px #00000026;
  border-radius: 25px;
  padding: 25px;
  margin-top: 20px;
  z-index: 2;
  margin: 0 auto;
  position: relative;
  padding-top: 30px;
  margin-bottom: 32px;
}

.breakdown-content {
  width: 100%;
}

.breakdown-row {
  display: flex;
  padding: 8px 5px;
}

.col {

  &.subtitle {
    flex: 4;
    font-size: 17px;
    text-align: left;
  }

  &.title {
    flex: 1;
    font-weight: 700;
    text-align: left;
    color: #202125;
    font-size: 25px;
  }
}

.arrow-right {
  margin-left: 10px;
  font-size: 20px;
}

.light-yellow-background-color {
  background-color: #FCF7ED;
  position: absolute;
  height: 120px;
  z-index: 0;
  bottom: 0;
  width: 100%;
}
</style>
